import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Wallet } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { useSetCryptoOnchainMerchantPayoutAddresses } from '@/lib/api/crypto-onchain-platform';

import { PayoutAddressFields } from './PayoutAddressFields';
import {
  diffPayoutAddresses,
  isPayoutAddressDraftValid,
  toPayoutAddressDraft,
  type PayoutAddressDraft,
} from './payout-address';
import type { PayoutAddressKind, PlatformMerchant } from './types';

type Props = {
  merchant: PlatformMerchant;
};

/**
 * Staff repair of a merchant's payout addresses (a merchant who lost access,
 * a wrong chain picked at enable time). Only the changed kinds are sent so an
 * address the mirror does not know about is never cleared by accident.
 */
export function MerchantPayoutAddressesDialog({ merchant }: Props) {
  const { t } = useTranslation('manageCryptoPayments');
  const setPayoutAddresses = useSetCryptoOnchainMerchantPayoutAddresses();

  const [isOpen, setIsOpen] = useState(false);
  const [draft, setDraft] = useState<PayoutAddressDraft>(() =>
    toPayoutAddressDraft(merchant.payoutAddresses),
  );

  const changes = diffPayoutAddresses(merchant.payoutAddresses, draft);
  const hasChanges = Object.keys(changes).length > 0;
  const canSave = hasChanges && isPayoutAddressDraftValid(draft) && !setPayoutAddresses.isPending;

  const handleOpenChange = (nextOpen: boolean) => {
    setIsOpen(nextOpen);
    if (nextOpen) setDraft(toPayoutAddressDraft(merchant.payoutAddresses));
  };

  const handleFieldChange = (kind: PayoutAddressKind, value: string) => {
    setDraft((current) => ({ ...current, [kind]: value }));
  };

  const handleSave = () => {
    if (!canSave) return;
    setPayoutAddresses.mutate(
      { storeId: merchant.storeId, payoutAddresses: changes },
      { onSuccess: () => setIsOpen(false) },
    );
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="sm">
          <Wallet className="h-3.5 w-3.5" />
          {t('merchants.editPayoutAddresses')}
        </Button>
      </DialogTrigger>

      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{t('payoutDialog.title', { store: merchant.storeName })}</DialogTitle>
          <DialogDescription>{t('payoutDialog.description')}</DialogDescription>
        </DialogHeader>

        <PayoutAddressFields
          values={draft}
          onChange={handleFieldChange}
          idPrefix={`merchant-payout-${merchant.storeId}`}
          isDisabled={setPayoutAddresses.isPending}
        />

        <DialogFooter className="items-center">
          {!hasChanges && (
            <span className="text-muted-foreground mr-auto text-xs">{t('wallets.noChanges')}</span>
          )}
          <Button variant="ghost" onClick={() => setIsOpen(false)}>
            {t('actions.cancel')}
          </Button>
          <Button disabled={!canSave} onClick={handleSave}>
            {setPayoutAddresses.isPending ? t('actions.saving') : t('payoutDialog.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
